import React, { useState, useEffect } from 'react';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { useTIN } from '@src/state/taxInformation';
import { useUser } from '@src/state/user';
import { useStandardUserRegistration } from '@src/state/SURegistration';
import { useEditing } from '@src/state/editing/hooks';
import { UserRegistrationShell } from '@src/common/UserRegistrationShell';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { PortalAccess } from '@src/common/PortalAccess';
import { useOnloading } from '@src/state/landing';
import { FLOW_ID } from '@src/common/AppConstants';
import {
  getOrgType,
  isValidPhone,
  adobeInteraction,
} from '../../../common/helpers';
import { permissions } from '@src/app/constants/permissions';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { config } from '@uhg-abyss/web/tools/config';
import { useOnStepper } from '@src/state/stepper';
import { StepperV2 } from '@src/common/StepperV2';
import { jobFunctions } from '@src/app/constants/jobFunctions';
import { AccessAdministrator } from '@src/common/AccessAdministrator';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { useDental } from '@src/state/dental';
import { AdminSelection } from './AdminSelection';
import { SUReviewAndConfirm } from './SUReviewAndConfirm';
import { AccountInfo } from '@src/common/AccountInfo';
import { JobFunctionV2 } from '@src/common/JobFunctionV2';
import { setPageHandler } from '@src/common/tools/analytics/helpers';

export const StandardUserRegistration = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');

  const form = useForm();
  const { pathname } = useLocation();
  const registrationSteps = {
    adminSelection: 'Access Administrator',
    accountInfo: 'Account Information',
    jobFunction: 'Job Functions',
    portalAccess: 'Portal Access',
    reviewConfirm: 'Review & Confirm',
    adminAttestation: 'Attestation',
  };
  const router = useRouter();
  const { registrationStep, token } = useParams();

  const [activeStep, setActiveStep] = useState('');
  const [stepper, setupStepper] = useState([]);
  const { TINInfo, TINNumber } = useTIN.state();
  const { userUUID } = useUser.state();
  const { resetTINInfo } = useTIN.actions();
  const {
    selectAdmin,
    setPhoneNumber,
    resetState,
    setJobFunctions,
    setPermissions,
    submitTINRequest,
  } = useStandardUserRegistration.actions();
  const { isEditing } = useEditing.state();
  const { setIsEditing } = useEditing.actions();
  const { currentFlow } = useOnloading.state();
  const {
    selectedJobFunctions,
    selectedPermissions,
    selectedAdmin,
    phone,
    phoneExt,
  } = useStandardUserRegistration.state();
  const { isDental } = useDental.state();

  const permissionsArray = [];
  selectedPermissions.forEach((permission) => {
    if (permission.subPermissions) {
      permission.subPermissions.forEach((subPermission) => {
        permissionsArray.push(subPermission);
      });
    }
    permissionsArray.push(permission);
  });
  const paaInfo = TINInfo?.paaInfo;
  const existingUser = !!(
    TINInfo &&
    TINInfo.existingUser &&
    TINInfo.paaInfo &&
    TINInfo.paaInfo.length > 1
  );
  const existingUserWithOnePaa = !!(
    TINInfo &&
    TINInfo.existingUser &&
    TINInfo.paaInfo &&
    TINInfo.paaInfo.length === 1
  );

  const { setCompleteStep, setCurrentStep, setStepper } =
    useOnStepper.actions();

  const navigationAllowed = () => {
    if (activeStep === 'Account Information') {
      form.validate('user.phone');
      if (!isValidPhone(phone)) {
        return false;
      }
    }
    return true;
  };

  const handleRouteStep = (step) => {
    const path = Object.keys(registrationSteps).find((key) => {
      return registrationSteps[key] === step;
    });
    setActiveStep(step);
    router.navigate(`/SURegistration/${path}/${token}`);
  };

  const handleSetActiveStep = (step) => {
    if (navigationAllowed() && step) {
      handleRouteStep(step);
    }
  };

  const handleNext = (location, payload) => {
    const current = stepper.find((el) => {
      return el.name === activeStep;
    }).step;
    setCompleteStep(current);
    adobeInteraction(
      activeStep,
      isEditing ? 'save' : 'next',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active,
      location || '',
      payload || {}
    );
    if (
      activeStep === 'Account Information' &&
      currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN
    ) {
      handleRouteStep(registrationSteps.jobFunction);
    } else if (
      activeStep === 'Portal Access' ||
      (activeStep !== 'Account Information' && isEditing) ||
      (activeStep === 'Account Information' &&
        currentFlow !== FLOW_ID.PROVIDER) ||
      (activeStep === 'Job Functions' &&
        currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN)
    ) {
      handleRouteStep(registrationSteps.reviewConfirm);
      setIsEditing(false);
    } else if (
      activeStep === 'Job Functions' ||
      (activeStep === 'Access Administrator' &&
        currentFlow === FLOW_ID.PROVIDER &&
        existingUser)
    ) {
      handleRouteStep(registrationSteps.portalAccess);
    } else if (
      activeStep === 'Access Administrator' ||
      (activeStep === 'Attestation' &&
        (currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN ||
          currentFlow === FLOW_ID.API_MARKETPLACE_BILLING_TECHNICIAN ||
          currentFlow === FLOW_ID.API_MARKETPLACE_VENDOR_TECHNICIAN))
    ) {
      handleRouteStep(registrationSteps.accountInfo);
    } else if (
      activeStep === 'Account Information' &&
      currentFlow === FLOW_ID.PROVIDER
    ) {
      handleRouteStep(registrationSteps.jobFunction);
    }
  };

  const handleBack = (location) => {
    const currentStep = stepper.findIndex((el) => {
      return el.name === activeStep;
    });
    adobeInteraction(
      activeStep,
      'back',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active,
      location || ''
    );
    if (currentStep === 0) {
      resetState();
      resetTINInfo();
      router.navigate(`/TINValidation/${currentFlow}/${token}`);
    } else {
      handleRouteStep(stepper[currentStep - 1].name);
    }
  };

  const handleEdit = (sectionName) => {
    setIsEditing(true);
    adobeInteraction(
      'review and confirm',
      'edit',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    if (sectionName === 'PAA') {
      handleRouteStep(registrationSteps.adminSelection);
    } else if (sectionName === 'Account Info') {
      handleRouteStep(registrationSteps.accountInfo);
    } else if (sectionName === 'Task Access') {
      handleRouteStep(registrationSteps.portalAccess);
    }
  };

  let rcmIndicator = false;
  let rcmCompanyName = '';
  let npiValue = '';

  const handleConfirm = () => {
    const handleError = () => {
      router.navigate(`/error-page/${token}`);
    };
    const arrayOfSelectedJobs = selectedJobFunctions
      ? Object.keys(selectedJobFunctions).map((key) => {
          return selectedJobFunctions[key];
        })
      : [];

    const processResponse = () => {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const tool = [];
        selectedPermissions.map((p) => {
          if (p.code === 'R') {
            tool.push('Reporting:');
            tool.push(
              p.subPermissions
                .map((n) => {
                  return n.name;
                })
                .join(', ')
            );
          } else {
            tool.push(p.permission);
          }
          return tool;
        });
        const orgType = getOrgType(currentFlow);

        adobeTaggingEvent('TIN_REGISTRATION_COMPLETION', {
          jobName: `tin registration-${orgType}`,
          orgType,
          jobFunctions:
            (arrayOfSelectedJobs &&
              arrayOfSelectedJobs
                .map((p) => {
                  return p.name;
                })
                .toString()) ||
            '',
          portalTaskAccess: (tool && tool.toString()) || '',
          providerUuid: userUUID,
          registrationTaxId: TINNumber,
          rcmOrBillingFlag: rcmIndicator === false ? 'No' : 'Yes',
          companyName: rcmCompanyName,
          npi: npiValue,
        });
        adobeTaggingEvent('TIN_REGISTRATION_FINAL_COMPLETE', {
          jobName: `tin registration-${orgType}`,
          orgType,
          jobFunctions:
            (arrayOfSelectedJobs &&
              arrayOfSelectedJobs
                .map((p) => {
                  return p.name;
                })
                .toString()) ||
            '',
          portalTaskAccess: (tool && tool.toString()) || '',
          providerUuid: userUUID,
          registrationTaxId: TINNumber,
          rcmOrBillingFlag: rcmIndicator === false ? 'No' : 'Yes',
          companyName: rcmCompanyName,
          npi: npiValue,
        });
      }
      router.navigate(`/Registration/EmailConfirmation/${token}`);
    };

    const renamedJobFunctions = arrayOfSelectedJobs.map((item) => {
      const jobFunction = { code: item.code, npi: item.npiValue };
      if (item.npiValue) {
        npiValue = item?.npiValue || '';
      }
      if (item.RCMValue) {
        rcmIndicator = item.RCMValue === 'Y';
        rcmCompanyName = item.RCMValue === 'Y' ? item.companyName : undefined;
      }
      return jobFunction;
    });

    const newSelectedPermissions = permissionsArray
      .filter((obj) => {
        return obj.permission !== 'Reporting';
      })
      .map((item) => {
        return { code: item.code, subCode: item.subCode || '' };
      });

    form.validate('SURegistration/POST_SUBMISSION', () => {
      const payload = {
        url:
          uiMessages.getRow('REGISTER_API')?.message || config('REGISTER_API'),
        currentFlow,
        requestId: TINInfo.requestId,
        phoneNumber: phone,
        phoneExt,
        rcmIndicator,
        rcmCompanyName,
        approvalUuid: selectedAdmin && selectedAdmin.userUUID,
        assignedAdmin:
          selectedAdmin &&
          `${selectedAdmin.firstName} ${selectedAdmin.lastName}`,
        assignedAdminId: selectedAdmin?.id || null,
        isDental,
      };
      if (
        currentFlow === FLOW_ID.PROVIDER ||
        currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN
      ) {
        if (TINInfo.existingUser) {
          payload.jobFunctions = TINInfo.existingUser?.jobFunctions;
          payload.phoneExt = TINInfo.existingUser?.phoneExt;
          payload.phoneNumber = TINInfo.existingUser?.phoneNumber;
        } else {
          payload.jobFunctions = renamedJobFunctions;
        }
        payload.permissions = newSelectedPermissions;
      }
      submitTINRequest(
        payload,
        (response) => {
          if (response.error) {
            if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
              adobeTaggingEvent('API_TRANSACTION_ERROR', {
                errorMessage: response.error.description,
                errorApiCode: response.error.code,
                errorHttpsCode: response.error.status,
              });
            }
            handleError();
            return;
          }
          processResponse();
        },
        handleError
      );
    });
  };

  useEffect(() => {
    const SUpageData = {
      [FLOW_ID.PROVIDER]: [
        {
          step: 1,
          name: 'Access Administrator',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Job Functions',
          complete: false,
        },
        {
          step: 4,
          name: 'Portal Access',
          complete: false,
        },
        {
          step: 5,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
      [FLOW_ID.BILLING]: [
        {
          step: 1,
          name: 'Access Administrator',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
      [FLOW_ID.VENDOR]: [
        {
          step: 1,
          name: 'Access Administrator',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
      [FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN]: [
        {
          step: 1,
          name: 'Attestation',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Job Functions',
          complete: false,
        },
        {
          step: 4,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
      [FLOW_ID.API_MARKETPLACE_BILLING_TECHNICIAN]: [
        {
          step: 1,
          name: 'Attestation',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
      [FLOW_ID.API_MARKETPLACE_VENDOR_TECHNICIAN]: [
        {
          step: 1,
          name: 'Attestation',
          complete: false,
        },
        {
          step: 2,
          name: 'Account Information',
          complete: false,
        },
        {
          step: 3,
          name: 'Review & Confirm',
          complete: false,
        },
      ],
    };

    if (currentFlow === FLOW_ID.PROVIDER && existingUserWithOnePaa) {
      SUpageData[currentFlow] = SUpageData[currentFlow]
        .filter((el) => {
          return (
            el.name !== 'Access Administrator' &&
            el.name !== 'Account Information' &&
            el.name !== 'Job Functions'
          );
        })
        .map((el, index) => {
          return { ...el, step: index + 1 };
        });
      setActiveStep(registrationSteps.portalAccess);
    } else if (currentFlow === FLOW_ID.PROVIDER && existingUser) {
      SUpageData[currentFlow] = SUpageData[currentFlow]
        .filter((el) => {
          return (
            el.name !== 'Account Information' && el.name !== 'Job Functions'
          );
        })
        .map((el, index) => {
          return { ...el, step: index + 1 };
        });
      setActiveStep(registrationSteps.adminSelection);
    } else if (
      paaInfo &&
      paaInfo.length === 1 &&
      currentFlow !== FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN &&
      currentFlow !== FLOW_ID.API_MARKETPLACE_BILLING_TECHNICIAN &&
      currentFlow !== FLOW_ID.API_MARKETPLACE_VENDOR_TECHNICIAN
    ) {
      if (SUpageData[currentFlow]) {
        SUpageData[currentFlow].shift();
      }
      SUpageData[currentFlow] = SUpageData[currentFlow]?.map((el) => {
        return { ...el, step: el.step - 1 };
      });
      setActiveStep(registrationSteps[registrationStep]);
    } else {
      setActiveStep(registrationSteps[registrationStep]);
    }
    setStepper({ data: SUpageData[currentFlow], save: true });
    setupStepper(SUpageData[currentFlow]);
  }, [paaInfo]);

  useEffect(() => {
    if (
      (activeStep === 'Account Information' || existingUserWithOnePaa) &&
      paaInfo &&
      paaInfo.length === 1
    ) {
      selectAdmin(paaInfo[0]);
    }
    if (paaInfo && paaInfo.length >= 1) {
      setCurrentStep(activeStep);
    }
  }, [activeStep, paaInfo]);

  useEffect(() => {
    if (
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active &&
      pathname.includes('accountInfo')
    ) {
      const orgType = getOrgType(currentFlow);
      let section1;
      let section2;
      let section3 = '';
      let pageName;
      if (TINInfo && !TINInfo?.hasPAA) {
        section1 = 'paa registration';
        section2 = 'account information';
        pageName = `digitalsecurity:${section1}:${section2}`;
      } else {
        section1 = 'tin registration';
        section2 = orgType;
        section3 = 'account information';
        pageName = `digitalsecurity:${section1}:${section2}:${section3}`;
      }
      setPageHandler('account information');
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3,
        siteSectionL3: 'tin registration',
        siteSectionL4: 'healthcare professional or facility',
      });
    }
  }, [pathname]);
  const renderStep = () => {
    switch (registrationStep) {
      case 'adminAttestation':
        return (
          <AccessAdministrator
            handleNext={handleNext}
            handleBack={handleBack}
          />
        );
      case 'adminSelection':
        return (
          <AdminSelection handleNext={handleNext} handleBack={handleBack} />
        );
      case 'accountInfo':
        return (
          <AccountInfo
            handleNext={() => handleNext('confirm your account information')}
            handleBack={() => handleBack('confirm your account information')}
            setPhoneNumber={setPhoneNumber}
            phone={phone}
            phoneExt={phoneExt}
          />
        );
      case 'jobFunction':
        return (
          <JobFunctionV2
            jobFunctions={jobFunctions}
            selectedJobFunctions={selectedJobFunctions}
            setJobFunctions={setJobFunctions}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 'portalAccess':
        return (
          <PortalAccess
            permissions={permissions}
            selectedPermissions={selectedPermissions}
            preserveSelectedPermissions={setPermissions}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 'reviewConfirm':
        return (
          <SUReviewAndConfirm
            handleEdit={handleEdit}
            handleConfirm={handleConfirm}
            handleBack={() => handleBack('review and confirm')}
          />
        );
      default:
        return null; // or redirect to a default step
    }
  };
  return (
    <UserRegistrationShell showProgressLink activeStep={activeStep}>
      <StepperV2 setActiveStep={handleSetActiveStep} />
      {renderStep()}
    </UserRegistrationShell>
  );
};
